import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import Base64FileEncoder from "../../components/Base64FileEncoder";
import {
  ArrowLeft,
  ArrowSquareOut,
  CheckCircle,
  Warning,
  XCircle,
  AppleLogo,
  GoogleLogo,
} from "@phosphor-icons/react";

/**
 * Apple / Google Wallet Credentials Guide
 *
 * The wallet *minting code* is already shipped end-to-end (see
 * /app/backend/routes/wallet.py — Apple .pkpass zip + Google Wallet save URL).
 * The only blocker between "code ready" and "Add to Apple/Google Wallet
 * buttons functional" is the user obtaining the platform credentials
 * (Apple Developer Pass Type ID + signing cert, Google Cloud service account)
 * and setting them as env vars on the deploy panel.
 *
 * This page walks them through each step + provides inline smoke-test
 * buttons that mint a throwaway pass against the configured credentials so
 * they can validate before going live.
 */

const APPLE_STEPS = [
  {
    n: 1,
    title: "Register a Pass Type ID with Apple",
    minutes: "5 min",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          In your Apple Developer account, register a new <b>Pass Type ID</b>{" "}
          identifier (reverse-DNS, e.g. <span className="mono">pass.com.zcardon.cashcard</span>).
          Note the identifier string — you'll set it as{" "}
          <span className="mono">APPLE_PASS_TYPE_ID</span>.
        </p>
        <a
          href="https://developer.apple.com/account/resources/identifiers/list/passTypeId"
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#184A30] text-sm font-medium mt-3"
        >
          Open Apple Developer → Pass Type IDs <ArrowSquareOut size={14} />
        </a>
        <p className="text-xs text-[#4A5C50] mt-3">
          Also grab your <b>10-character Team ID</b> from{" "}
          <span className="mono">Membership</span> page — set it as{" "}
          <span className="mono">APPLE_TEAM_ID</span>.
        </p>
      </>
    ),
  },
  {
    n: 2,
    title: "Generate + download the Pass Type ID signing certificate",
    minutes: "10 min",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          On your Mac, open <b>Keychain Access</b> → <i>Certificate Assistant</i> →{" "}
          <i>Request a Certificate From a Certificate Authority</i>. Save the{" "}
          <span className="mono">.certSigningRequest</span> file.
        </p>
        <p className="text-[#4A5C50] leading-relaxed mt-2">
          Back in Apple Developer → your Pass Type ID → <i>Create Certificate</i>,
          upload the CSR, then download the resulting{" "}
          <span className="mono">.cer</span>. Double-click to import into Keychain,
          right-click the cert → <i>Export…</i> as{" "}
          <span className="mono">.p12</span> with a strong password.
        </p>
        <p className="text-xs text-[#4A5C50] mt-3">
          Set the password as <span className="mono">APPLE_PASS_CERT_PASSWORD</span>{" "}
          in the Deploy Panel. Then base64-encode the .p12 using the drop-zone below
          (or <span className="mono">base64 -i cert.p12 | pbcopy</span> on macOS).
        </p>
        <Base64FileEncoder
          label="Apple Pass Type ID .p12"
          accept=".p12,.pfx"
          envVar="APPLE_PASS_CERT_P12_B64"
        />
      </>
    ),
  },
  {
    n: 3,
    title: "WWDR intermediate certificate (G4)",
    minutes: "1 min (optional)",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          The Apple WWDR-G4 intermediate cert is required to chain trust.
          Zcardon ships a bundled copy at{" "}
          <span className="mono">/app/backend/assets/AppleWWDRCAG4.pem</span>{" "}
          so you can skip this step.
        </p>
        <p className="text-xs text-[#4A5C50] mt-3">
          If Apple rotates WWDR, override via{" "}
          <span className="mono">APPLE_WWDR_CERT_PEM_B64</span> (base64-encoded PEM).
        </p>
      </>
    ),
  },
];

const GOOGLE_STEPS = [
  {
    n: 1,
    title: "Activate Google Wallet API + create an Issuer",
    minutes: "15 min",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          Sign in to Google Pay & Wallet Console with the Google account that
          owns your business. Enable the Wallet API on a new GCP project,
          then submit the Issuer-onboarding form (Google sales-reviews — takes
          a few business days).
        </p>
        <a
          href="https://pay.google.com/business/console/"
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#184A30] text-sm font-medium mt-3"
        >
          Open Google Pay & Wallet Console <ArrowSquareOut size={14} />
        </a>
        <p className="text-xs text-[#4A5C50] mt-3">
          When approved, copy your <b>Issuer ID</b> (a long numeric, e.g.{" "}
          <span className="mono">3388000000022xxxxxx</span>) and set it as{" "}
          <span className="mono">GOOGLE_WALLET_ISSUER_ID</span>.
        </p>
      </>
    ),
  },
  {
    n: 2,
    title: "Create a service account + download the JSON key",
    minutes: "5 min",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          In Google Cloud Console → IAM → Service Accounts, create one (e.g.{" "}
          <span className="mono">zcardon-wallet</span>). Grant it the{" "}
          <b>Wallet Object Issuer</b> role on the Wallet API. Generate a JSON
          key and download it.
        </p>
        <a
          href="https://console.cloud.google.com/iam-admin/serviceaccounts"
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#184A30] text-sm font-medium mt-3"
        >
          Open Google Cloud → Service Accounts <ArrowSquareOut size={14} />
        </a>
        <p className="text-xs text-[#4A5C50] mt-3">
          Drop the downloaded JSON file into the encoder below to get the
          base64 string, then paste it as{" "}
          <span className="mono">GOOGLE_WALLET_SERVICE_ACCOUNT_JSON_B64</span>{" "}
          in the Deploy Panel.
        </p>
        <Base64FileEncoder
          label="Google service-account JSON"
          accept=".json,application/json"
          envVar="GOOGLE_WALLET_SERVICE_ACCOUNT_JSON_B64"
          validateJson
        />
      </>
    ),
  },
  {
    n: 3,
    title: "(Optional) Create a Loyalty Class",
    minutes: "2 min",
    body: (
      <>
        <p className="text-[#4A5C50] leading-relaxed">
          Zcardon auto-creates a default <span className="mono">zcardon_cash_card</span>{" "}
          class on first save. If you want a branded class, create it via the
          Wallet API REST or Console, then set{" "}
          <span className="mono">GOOGLE_WALLET_CLASS_ID</span> to its identifier.
        </p>
      </>
    ),
  },
];

function StatusPill({ ok, configuredLabel = "Ready", missingLabel = "Not configured" }) {
  return ok ? (
    <span className="inline-flex items-center gap-1.5 text-emerald-700 text-xs font-medium">
      <CheckCircle size={14} weight="fill" /> {configuredLabel}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-[#B04E2F] text-xs font-medium">
      <XCircle size={14} weight="fill" /> {missingLabel}
    </span>
  );
}

function SmokeTest({ provider }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const label = provider === "apple" ? "Apple Wallet" : "Google Wallet";

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post(`/admin/wallet/test-${provider}`);
      setResult(r.data);
    } catch (e) {
      setResult({
        ok: false,
        stage: "request_failed",
        error: e?.response?.data?.detail || e?.message || "Request failed",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4" data-testid={`wallet-guide-smoke-${provider}`}>
      <Button
        size="sm"
        onClick={run}
        disabled={busy}
        className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white text-xs"
        data-testid={`wallet-guide-smoke-${provider}-btn`}
      >
        {busy ? "Testing…" : `Run ${label} smoke test`}
      </Button>
      {result && (
        <div
          className={`mt-3 rounded-lg border p-3 text-xs ${
            result.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-rose-50 border-rose-200 text-rose-900"
          }`}
          data-testid={`wallet-guide-smoke-${provider}-result`}
        >
          <p className="font-semibold mb-1">
            {result.ok ? `✓ ${label} credentials are valid` : `✗ Failed at stage: ${result.stage || "unknown"}`}
          </p>
          {result.error && <p className="mono break-all opacity-90">{result.error}</p>}
          {result.missing && result.missing.length > 0 && (
            <p className="mono opacity-90">Missing: {result.missing.join(", ")}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function WalletCredentialsGuide() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/wallet/config");
      setConfig(r.data);
    } catch {
      setConfig({ apple_enabled: false, google_enabled: false, error: true });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-[#FBF9F6] text-[#112318]" data-testid="wallet-credentials-guide">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/admin/setup" className="inline-flex items-center gap-1 text-sm text-[#4A5C50] hover:text-[#184A30]" data-testid="wallet-guide-back">
          <ArrowLeft size={14} /> Back to Setup Checklist
        </Link>
        <header className="mt-6">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Premium · Add-to-Wallet</p>
          <h1 className="serif text-4xl mt-1">Apple Pay / Google Pay credentials</h1>
          <p className="text-[#4A5C50] mt-3 leading-relaxed max-w-2xl">
            All the wallet code — Apple <span className="mono">.pkpass</span> minting,
            Google Wallet save-URL signing, smoke tests, adoption metrics — is
            already shipped. The only thing left is for <b>you</b> to obtain
            the developer credentials from Apple + Google and add them as env
            vars on the Deploy Panel. This page walks you through it.
          </p>
        </header>

        {/* Live config status */}
        <section className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid="wallet-guide-status">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Current configuration</p>
              <h2 className="serif text-2xl mt-0.5">Credential readiness</h2>
            </div>
            <Button size="sm" variant="outline" onClick={refresh} disabled={loading} className="rounded-xl text-xs border-[#184A30] text-[#184A30]" data-testid="wallet-guide-refresh">
              {loading ? "Checking…" : "Refresh"}
            </Button>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-[#E5E0D8] p-3" data-testid="wallet-guide-apple-status">
              <div className="flex items-center gap-2">
                <AppleLogo size={20} weight="fill" className="text-[#112318]" />
                <p className="font-medium text-sm">Apple Wallet</p>
              </div>
              <p className="mt-2"><StatusPill ok={!!config?.apple_enabled} /></p>
            </div>
            <div className="rounded-xl border border-[#E5E0D8] p-3" data-testid="wallet-guide-google-status">
              <div className="flex items-center gap-2">
                <GoogleLogo size={20} weight="fill" className="text-[#112318]" />
                <p className="font-medium text-sm">Google Wallet</p>
              </div>
              <p className="mt-2"><StatusPill ok={!!config?.google_enabled} /></p>
            </div>
          </div>
        </section>

        {/* Web-vs-native warning */}
        <section className="mt-6 rounded-2xl border border-[#CBA35C]/40 bg-[#CBA35C]/5 p-5" data-testid="wallet-guide-warning">
          <div className="flex items-start gap-2">
            <Warning size={18} weight="fill" className="text-[#8C6B21] mt-0.5 shrink-0" />
            <div className="text-sm text-[#8C6B21] leading-relaxed">
              <p><b>Static passes</b> (the kind we mint here) work fine from web — receivers tap "Add to Apple/Google Wallet" on their phone browser and the card lands in their wallet.</p>
              <p className="mt-2"><b>Push provisioning</b> (the "tap your physical card" feature) is <b>iOS/Android native-only</b> — Apple Pay specifically forbids it from web. To ship the polished native experience, you need iOS + Android apps + additional entitlements. For the immediate web launch the static-pass flow is enough.</p>
            </div>
          </div>
        </section>

        {/* Pre-flight prerequisites */}
        <section className="mt-6 rounded-2xl border border-[#184A30]/30 bg-white p-5" data-testid="wallet-guide-prereqs">
          <div className="flex items-center gap-2">
            <CheckCircle size={18} weight="fill" className="text-[#184A30]" />
            <h2 className="serif text-xl">Before you start — what you&apos;ll need</h2>
          </div>
          <p className="text-xs text-[#4A5C50] mt-1 leading-relaxed">
            Quick pre-flight so you don&apos;t hit a wall halfway through. Apple is the heavier lift (needs a Mac + a paid Apple Developer membership). Google is web-only and free, but Google&apos;s review of the Issuer onboarding form takes 1–5 business days, so submit it first thing.
          </p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl border border-[#E5E0D8] p-4">
              <div className="flex items-center gap-2">
                <AppleLogo size={16} weight="fill" /> <p className="font-semibold">Apple Wallet — checklist</p>
              </div>
              <ul className="mt-2 list-disc list-inside text-xs text-[#4A5C50] space-y-1.5">
                <li>An <b>Apple Developer Program</b> membership (US$99/year). <a href="https://developer.apple.com/programs/enroll/" target="_blank" rel="noopener noreferrer" className="text-[#184A30] underline">Enroll →</a></li>
                <li>A <b>Mac</b> with Keychain Access (used once, to generate the CSR + export the .p12). No Mac? See FAQ below.</li>
                <li>Your <b>10-character Team ID</b> (visible on Apple Developer → Membership)</li>
                <li>A short password you&apos;ll use to protect the .p12 export — keep it safe; we store it as <span className="mono">APPLE_PASS_CERT_PASSWORD</span></li>
              </ul>
            </div>
            <div className="rounded-xl border border-[#E5E0D8] p-4">
              <div className="flex items-center gap-2">
                <GoogleLogo size={16} weight="fill" /> <p className="font-semibold">Google Wallet — checklist</p>
              </div>
              <ul className="mt-2 list-disc list-inside text-xs text-[#4A5C50] space-y-1.5">
                <li>A <b>Google Cloud project</b> (free tier is fine; create one if you don&apos;t already have one). <a href="https://console.cloud.google.com/projectcreate" target="_blank" rel="noopener noreferrer" className="text-[#184A30] underline">Create →</a></li>
                <li>The Google account that will manage your business&apos;s wallet (use your <b>operations</b> email, not your personal Gmail)</li>
                <li>Your business&apos;s legal name + website (Google reviews these — must match LLC records). For Zcardon: <span className="mono">Zeborg Water Solution Corp.</span> / <span className="mono">zcardon.com</span></li>
                <li>~10 minutes today to submit the Issuer onboarding form — Google replies in 1-5 business days</li>
              </ul>
            </div>
          </div>
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-3 text-xs">
            <p className="font-semibold inline-flex items-center gap-1.5"><Warning size={12} weight="fill" /> No Mac?</p>
            <p className="mt-1 leading-relaxed">
              You can still get a .p12 if you have access to <b>any</b> Mac for ~10 minutes (a friend&apos;s, an Apple Store demo unit, or a cloud Mac like MacInCloud / MacStadium &lt; $2/hr). The CSR step is the only Mac-only part — once you have the .p12, you can manage everything else from a browser.
            </p>
          </div>
        </section>

        {/* Apple Wallet section */}
        <section className="mt-8" data-testid="wallet-guide-apple-section">
          <div className="flex items-center gap-2">
            <AppleLogo size={22} weight="fill" className="text-[#112318]" />
            <h2 className="serif text-2xl">Apple Wallet setup</h2>
          </div>
          <p className="text-xs text-[#4A5C50] mt-1">
            ~30 min total wall-clock · requires Apple Developer account (US$99/year)
          </p>
          <ol className="mt-4 space-y-4">
            {APPLE_STEPS.map((s) => (
              <li key={s.n} className="rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid={`wallet-guide-apple-step-${s.n}`}>
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <h3 className="serif text-lg flex items-center gap-3">
                    <span className="text-[#4A5C50] mono text-xs">{s.n}</span> {s.title}
                  </h3>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">{s.minutes}</span>
                </div>
                <div className="mt-3 text-sm">{s.body}</div>
              </li>
            ))}
          </ol>
          <div className="mt-4 rounded-2xl border border-[#184A30]/30 bg-[#184A30]/5 p-4">
            <p className="text-xs text-[#184A30] mb-2 tracking-[0.2em] uppercase">Validate Apple credentials</p>
            <p className="text-sm text-[#4A5C50] leading-relaxed">
              Once all four <span className="mono">APPLE_*</span> env vars are set
              and the pod redeploys, this button mints a throwaway .pkpass
              against a synthetic card and surfaces the exact stage that fails
              (env_check / p12_decode / p12_open / pkpass_build).
            </p>
            <SmokeTest provider="apple" />
          </div>
        </section>

        {/* Google Wallet section */}
        <section className="mt-10" data-testid="wallet-guide-google-section">
          <div className="flex items-center gap-2">
            <GoogleLogo size={22} weight="fill" className="text-[#112318]" />
            <h2 className="serif text-2xl">Google Wallet setup</h2>
          </div>
          <p className="text-xs text-[#4A5C50] mt-1">
            ~20 min self-serve · free, but Google reviews the Issuer onboarding form (1-5 business days)
          </p>
          <ol className="mt-4 space-y-4">
            {GOOGLE_STEPS.map((s) => (
              <li key={s.n} className="rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid={`wallet-guide-google-step-${s.n}`}>
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <h3 className="serif text-lg flex items-center gap-3">
                    <span className="text-[#4A5C50] mono text-xs">{s.n}</span> {s.title}
                  </h3>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">{s.minutes}</span>
                </div>
                <div className="mt-3 text-sm">{s.body}</div>
              </li>
            ))}
          </ol>
          <div className="mt-4 rounded-2xl border border-[#184A30]/30 bg-[#184A30]/5 p-4">
            <p className="text-xs text-[#184A30] mb-2 tracking-[0.2em] uppercase">Validate Google credentials</p>
            <p className="text-sm text-[#4A5C50] leading-relaxed">
              Once both <span className="mono">GOOGLE_WALLET_ISSUER_ID</span> and{" "}
              <span className="mono">GOOGLE_WALLET_SERVICE_ACCOUNT_JSON_B64</span>{" "}
              are set, this signs a preview save-URL JWT against a synthetic
              card so you can verify the service-account permissions before
              shipping the "Add to Google Wallet" button to users.
            </p>
            <SmokeTest provider="google" />
          </div>
        </section>

        <footer className="mt-10 text-xs text-[#4A5C50]">
          Both providers gracefully no-op when their env vars aren't set —{" "}
          <span className="mono">/api/wallet/config</span> returns
          <span className="mono"> &#123;apple_enabled: false, google_enabled: false&#125;</span> and the
          frontend hides the corresponding buttons. So you can configure
          Apple and Google independently / in any order.
        </footer>
      </div>
    </div>
  );
}
